import type { ScreenMonitor } from '@viewer/protocol';
import { useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type RefObject } from 'react';
import { INSIDE_APP } from '../../lib/config';
import type { ActiveSession, RemoteCursorListener } from '../../lib/session/controller';
import { GUIDE_POINTER, pointerStyle } from './remoteCursor';
import { attachRemoteInput, frameToPointer, pointerToFrame, viewerTransferAccess, type SendInput } from './remoteInput';
import styles from './ViewerScreen.module.css';

/**
 * Tamanho da imagem nesta tela; a proporção é sempre a do computador remoto.
 * `fit` ajusta à janela, `shrink` ajusta sem passar do tamanho original e um número é
 * uma escala fixa (1 = um pixel da tela remota por pixel do monitor).
 */
export type ScreenZoom = 'fit' | 'shrink' | number;

interface Props extends HTMLAttributes<HTMLDivElement> {
  session: ActiveSession;
  /** Monitor recortado do frame; `null` mostra a imagem inteira, com todos os monitores. */
  monitor: ScreenMonitor | null;
  zoom: ScreenZoom;
  surfaceRef: RefObject<HTMLDivElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  sendInput: SendInput;
  watchRemoteCursor(listener: RemoteCursorListener): () => void;
  onAppPaste(): void;
  onBrowserPaste(data: DataTransfer): boolean;
}

/**
 * Vídeo do computador remoto com mouse, teclado e cursor. Serve à janela principal e às
 * janelas dos monitores desacoplados, que mostram só um pedaço do mesmo vídeo.
 */
export function RemoteScreen({
  session,
  monitor,
  zoom,
  surfaceRef,
  videoRef,
  sendInput,
  watchRemoteCursor,
  onAppPaste,
  onBrowserPaste,
  children,
  ...surfaceProps
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cursorMarkRef = useRef<HTMLImageElement>(null);
  const blockedRef = useRef<HTMLSpanElement>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const [pixelRatio, setPixelRatio] = useState(1);

  // A escala fixa conta pixels do monitor; a janela desacoplada pode estar num monitor com outro DPI.
  useEffect(() => {
    const view = surfaceRef.current?.ownerDocument.defaultView;
    if (!view) return;
    const update = () => setPixelRatio(view.devicePixelRatio);
    update();
    view.addEventListener('resize', update);
    return () => view.removeEventListener('resize', update);
  }, [surfaceRef]);

  // Sempre mudo, para o autoplay nunca ser bloqueado; o som toca no <audio> da janela principal.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = session.remoteStream;
    if (session.remoteStream) video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [session.remoteStream, videoRef]);

  const canControl = session.info.access === 'control';
  const transfer = viewerTransferAccess(session.info);

  // Captura de mouse e teclado enquanto o canal de entrada estiver aberto (e a sessão permitir controle).
  useEffect(() => {
    const surface = surfaceRef.current;
    const video = videoRef.current;
    if (!surface || !video || !session.inputOpen || !canControl) return;
    surface.focus();
    const toFrame = (clientX: number, clientY: number) =>
      pointerToFrame(
        clientX,
        clientY,
        video.getBoundingClientRect(),
        video.videoWidth,
        video.videoHeight,
        monitor ?? undefined,
      );
    return attachRemoteInput(
      surface,
      toFrame,
      sendInput,
      transfer.paste ? { insideApp: INSIDE_APP, onAppPaste, onBrowserPaste } : undefined,
      INSIDE_APP,
    );
  }, [session.inputOpen, canControl, transfer.paste, monitor, sendInput, onAppPaste, onBrowserPaste, surfaceRef, videoRef]);

  const mouseBlocked = canControl && session.mouseBlocked;
  // O cursor da pessoa do Host é desenhado no modo guia, ao lado da seta verde deste lado, e
  // sem controle, quando o ponteiro local não tem relação com o remoto.
  const markImage = !canControl || mouseBlocked ? (session.remoteCursor?.image ?? null) : null;

  // Imagem maior que a janela rola com o mouse: a posição do ponteiro na janela é a posição na
  // imagem, então as bordas da janela chegam às bordas da tela remota. Na captura, para rolar antes
  // de `attachRemoteInput` converter o ponteiro em pixels do frame.
  useEffect(() => {
    const surface = surfaceRef.current;
    const scroller = scrollerRef.current;
    if (!surface || !scroller) return;
    const onPointerMove = (event: PointerEvent) => {
      const rect = scroller.getBoundingClientRect();
      const { clientWidth, clientHeight } = scroller;
      const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / clientWidth));
      const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / clientHeight));
      scroller.scrollLeft = x * (scroller.scrollWidth - clientWidth);
      scroller.scrollTop = y * (scroller.scrollHeight - clientHeight);
    };
    surface.addEventListener('pointermove', onPointerMove, { capture: true });
    return () => surface.removeEventListener('pointermove', onPointerMove, { capture: true });
  }, [surfaceRef]);

  // A posição vai direto no elemento, sem passar pelo React. Rolar a imagem ampliada também a move.
  useEffect(() => {
    const mark = cursorMarkRef.current;
    const video = videoRef.current;
    const surface = surfaceRef.current;
    const scroller = scrollerRef.current;
    if (!mark || !video || !surface || !scroller) return;
    let last: { x: number; y: number } | null = null;
    const place = () => {
      const point =
        last && (!monitor || contains(monitor, last))
          ? frameToPointer(last.x, last.y, rectInside(video, surface), video.videoWidth, video.videoHeight)
          : null;
      mark.hidden = !point;
      if (point) mark.style.transform = `translate(${point.x}px, ${point.y}px)`;
    };
    const stop = watchRemoteCursor((position) => {
      last = position;
      place();
    });
    scroller.addEventListener('scroll', place);
    return () => {
      stop();
      scroller.removeEventListener('scroll', place);
    };
  }, [markImage, monitor, zoom, pixelRatio, watchRemoteCursor, surfaceRef, videoRef]);

  // Etiqueta "Guia" junto ao ponteiro enquanto a pessoa do outro lado usa o mouse físico.
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const place = () => {
      const badge = blockedRef.current;
      const pointer = pointerRef.current;
      if (!badge || !pointer) return;
      badge.style.transform = `translate(${pointer.x}px, ${pointer.y}px)`;
      badge.hidden = false;
    };
    const onPointerMove = (event: PointerEvent) => {
      const rect = surface.getBoundingClientRect();
      pointerRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      place();
    };
    place();
    surface.addEventListener('pointermove', onPointerMove);
    return () => surface.removeEventListener('pointermove', onPointerMove);
  }, [mouseBlocked, surfaceRef]);

  const frame = session.screen
    ? frameStyles(
        monitor ?? { x: 0, y: 0, width: session.screen.frameWidth, height: session.screen.frameHeight },
        session.screen,
        zoom,
        pixelRatio,
      )
    : null;

  return (
    <div
      {...surfaceProps}
      ref={surfaceRef}
      className={styles.surface}
      style={{
        cursor: !canControl ? undefined : mouseBlocked ? GUIDE_POINTER : pointerStyle(session.remoteCursor),
      }}
      tabIndex={0}
      aria-label={
        canControl
          ? 'Tela do computador remoto. Use o mouse e o teclado para controlar.'
          : 'Tela do computador remoto, somente visualização.'
      }
    >
      <div ref={scrollerRef} className={styles.scroller}>
        <div className={styles.viewport} style={frame?.viewport}>
          <video ref={videoRef} className={styles.video} style={frame?.video} autoPlay muted playsInline />
        </div>
      </div>
      {markImage && (
        <img
          ref={cursorMarkRef}
          className={styles.remoteCursor}
          src={markImage.url}
          alt=""
          style={{ left: -markImage.hotspotX, top: -markImage.hotspotY }}
          hidden
        />
      )}
      {mouseBlocked && (
        <span ref={blockedRef} className={styles.blocked} hidden>
          Guia
        </span>
      )}
      {children}
    </div>
  );
}

function contains(monitor: ScreenMonitor, point: { x: number; y: number }) {
  return (
    point.x >= monitor.x &&
    point.x < monitor.x + monitor.width &&
    point.y >= monitor.y &&
    point.y < monitor.y + monitor.height
  );
}

/** Retângulo do vídeo em relação à superfície, onde o cursor remoto é posicionado. */
function rectInside(video: HTMLElement, surface: HTMLElement) {
  const box = video.getBoundingClientRect();
  const origin = surface.getBoundingClientRect();
  return { left: box.left - origin.left, top: box.top - origin.top, width: box.width, height: box.height };
}

/**
 * Moldura do vídeo só com CSS: tem a proporção da região mostrada (um monitor recortado ou
 * o frame inteiro) e cabe na superfície (unidades `cq*` da superfície) ou segue uma escala
 * fixa; dentro dela o vídeo inteiro fica ampliado e deslocado até a região.
 * Assim a janela não depende de medir nada a cada redimensionamento.
 */
function frameStyles(
  region: { x: number; y: number; width: number; height: number },
  frame: { frameWidth: number; frameHeight: number },
  zoom: ScreenZoom,
  pixelRatio: number,
): { viewport: CSSProperties; video: CSSProperties } {
  const { x, y, width, height } = region;
  const fitWidth = ['100cqw', `${(width / height) * 100}cqh`];
  const fitHeight = ['100cqh', `${(height / width) * 100}cqw`];
  if (zoom === 'shrink') {
    fitWidth.push(`${width / pixelRatio}px`);
    fitHeight.push(`${height / pixelRatio}px`);
  }
  return {
    viewport:
      typeof zoom === 'number'
        ? { width: `${(width * zoom) / pixelRatio}px`, height: `${(height * zoom) / pixelRatio}px` }
        : { width: `min(${fitWidth.join(', ')})`, height: `min(${fitHeight.join(', ')})` },
    video: {
      left: `${(-x / width) * 100}%`,
      top: `${(-y / height) * 100}%`,
      width: `${(frame.frameWidth / width) * 100}%`,
      height: `${(frame.frameHeight / height) * 100}%`,
    },
  };
}
